import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import i18n from "../i18n";
import { validateTheme } from "./validateTheme";

// Current stock appearance — an org with no branding configured and no
// override sees exactly this, unchanged.
const DEFAULT_THEME = {
  primaryColor: "#255CFF",
  backgroundColor: "#F0F8FF",
  titleColor: "#000000",
  subtitleColor: "#737373",
  bodyColor: "#737373",
  logoUrl: null,
  language: "en",
};

const ThemeContext = createContext(DEFAULT_THEME);

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * Picks black or white for text/icons that must stay readable against an
 * org's customizable background color, rather than assuming a light
 * background the way plain black text does. YIQ perceived-brightness
 * formula (standard, cheap contrast heuristic) — not full WCAG contrast,
 * but enough to keep e.g. "powered by" legible on a dark custom background.
 */
function contrastTextColor(hexColor) {
  if (!HEX_COLOR_REGEX.test(hexColor)) return "#000000";
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 128 ? "#FFFFFF" : "#000000";
}

/**
 * Converts a hex color to the "H S% L%" triplet format NextUI's own theme
 * CSS variables use (e.g. `--nextui-primary: 212.02 100% 46.67%`). Lets us
 * override NextUI's primary color at runtime by setting its CSS vars
 * directly, so every NextUI component (Button, Input, CircularProgress,
 * Progress, Listbox, ...) picks up the org's branding without each one
 * needing its own color prop or inline-style override.
 */
export function hexToHslTriplet(hexColor) {
  if (!HEX_COLOR_REGEX.test(hexColor)) return "0 0% 0%";
  const r = parseInt(hexColor.slice(1, 3), 16) / 255;
  const g = parseInt(hexColor.slice(3, 5), 16) / 255;
  const b = parseInt(hexColor.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${(h * 360).toFixed(2)} ${(s * 100).toFixed(2)}% ${(l * 100).toFixed(2)}%`;
}

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Merges the three theme layers, highest precedence first:
 *   1. override   — query param at load, postMessage update thereafter
 *   2. orgTheme    — org-wide default fetched from core (unset until the
 *                    runtime fetch is wired up)
 *   3. DEFAULT_THEME
 */
function resolveTheme(orgTheme, override) {
  return {
    ...DEFAULT_THEME,
    ...(orgTheme || {}),
    ...(override || {}),
  };
}

/**
 * Wraps the app, exposing the resolved theme via context (for components
 * that need the raw value, e.g. Logo's logoUrl-vs-inline-SVG branch) and via
 * CSS custom properties on a wrapper div (for Tailwind/inline-style
 * consumers). Scoped to this wrapper, not document.documentElement, so it
 * can't leak into a host page if this app is ever rendered outside an
 * iframe.
 *
 * `expectedParentOrigin` enables a dashboard-preview-only postMessage
 * channel for live theme updates without a reload — the real SDK embed
 * (urtentic.js) never sets this, so it never registers the listener. Only
 * messages from that exact origin, of type 'urtentic:themeUpdate', update
 * the live override; everything else is ignored, mirroring urtentic.js's
 * own exact-origin check on its iframe-to-parent channel.
 */
export function ThemeProvider({ orgTheme, override, expectedParentOrigin, children }) {
  const [liveOverride, setLiveOverride] = useState(override);

  useEffect(() => {
    if (!expectedParentOrigin) return undefined;

    function handleMessage(event) {
      if (event.origin !== expectedParentOrigin) return;
      const data = event.data;
      if (data && data.type === "urtentic:themeUpdate") {
        setLiveOverride(validateTheme(data.theme));
      }
    }

    window.addEventListener("message", handleMessage);
    // Tell the parent our listener is up. Without this, the parent's very
    // first theme push races this iframe's own load/hydrate — postMessage
    // doesn't queue for a listener that isn't registered yet, so a message
    // sent before this line runs is silently lost with no delivery
    // confirmation. See BrandingPreview.tsx's matching "previewReady"
    // handler in the dashboard app.
    window.parent.postMessage({ type: "urtentic:previewReady" }, expectedParentOrigin);
    return () => window.removeEventListener("message", handleMessage);
  }, [expectedParentOrigin]);

  const theme = useMemo(() => resolveTheme(orgTheme, liveOverride), [orgTheme, liveOverride]);

  useEffect(() => {
    if (i18n.language !== theme.language) {
      i18n.changeLanguage(theme.language);
    }
  }, [theme.language]);

  const primaryHsl = hexToHslTriplet(theme.primaryColor);
  const primaryForegroundHsl = hexToHslTriplet(contrastTextColor(theme.primaryColor));
  const onBackgroundColor = contrastTextColor(theme.backgroundColor);

  const cssVars = {
    "--urtentic-sdk-primary": theme.primaryColor,
    "--urtentic-sdk-background": theme.backgroundColor,
    "--urtentic-sdk-title": theme.titleColor,
    "--urtentic-sdk-subtitle": theme.subtitleColor,
    "--urtentic-sdk-body": theme.bodyColor,
    "--urtentic-sdk-on-background": onBackgroundColor,
    // Overrides NextUI's own theme vars so its components (Button, Input,
    // CircularProgress, Progress, Listbox, ...) follow the org's primary
    // color without per-component color props or inline-style patches.
    "--nextui-primary": primaryHsl,
    "--nextui-primary-foreground": primaryForegroundHsl,
    "--nextui-focus": primaryHsl,
    // NextUI's flat/light variants (e.g. CodeValidator's "Resend code"
    // button) read a specific numbered shade instead of the bare `primary`
    // token — text-primary-600 on a bg-primary/20 fill, plus 50/100/etc.
    // elsewhere (Input, Alert, Select, Calendar). Rather than generating a
    // real tint/shade ramp from one hex (a separate, much larger effort),
    // every shade maps to the same primaryHsl — flat, but always the right
    // hue instead of NextUI's static default blue.
    "--nextui-primary-50": primaryHsl,
    "--nextui-primary-100": primaryHsl,
    "--nextui-primary-200": primaryHsl,
    "--nextui-primary-300": primaryHsl,
    "--nextui-primary-400": primaryHsl,
    "--nextui-primary-500": primaryHsl,
    "--nextui-primary-600": primaryHsl,
    "--nextui-primary-700": primaryHsl,
    "--nextui-primary-800": primaryHsl,
    "--nextui-primary-900": primaryHsl,
    height: "100%",
    width: "100%",
  };

  // Paints the actual document background (not just a CSS var scoped to this
  // wrapper div) so a custom backgroundColor still shows instead of the
  // browser's default white in any gap outside this React tree's own boxes —
  // e.g. the area a mobile keyboard's viewport-pan reveals beyond the
  // fixed-position #F0F8FF-vs-custom `body` box on iOS Safari (see
  // public/index.html's --vh fallback and index.css's `body` rule). Safe to
  // touch this document's real body/html: direct-link only ever runs inside
  // its own iframe (web-sdk's <urtentic-modal>), never inline in a host
  // page, so this can't leak into anyone else's page.
  useEffect(() => {
    document.documentElement.style.backgroundColor = theme.backgroundColor;
    document.body.style.backgroundColor = theme.backgroundColor;
    return () => {
      document.documentElement.style.backgroundColor = "";
      document.body.style.backgroundColor = "";
    };
  }, [theme.backgroundColor]);

  return (
    <ThemeContext.Provider value={theme}>
      <div data-testid="theme-provider-root" style={cssVars}>{children}</div>
    </ThemeContext.Provider>
  );
}
